/* Call Recordings — intercom + PBX */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Microphone, DownloadSimple, SpeakerHigh, Trash, Broadcast } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Tabs = ({ value, onChange, items }) => (
    <div className="flex gap-1 mb-4 border-b" style={{ borderColor: 'var(--border-default)' }}>
        {items.map(([key, label, count]) => (
            <button key={key} data-testid={`rec-tab-${key}`}
                    onClick={() => onChange(key)}
                    className="px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 -mb-px"
                    style={{
                        borderColor: value === key ? 'var(--brand)' : 'transparent',
                        color: value === key ? 'var(--brand)' : 'var(--text-secondary)',
                    }}>
                {label} {count !== undefined && <span className="ml-2 text-xs opacity-70">({count})</span>}
            </button>
        ))}
    </div>
);

const Recordings = () => {
    const [tab, setTab] = useState('intercom');
    const [intercom, setIntercom] = useState([]);
    const [pbx, setPbx] = useState([]);
    const [loading, setLoading] = useState(true);
    const [playing, setPlaying] = useState(null);

    const load = () => {
        setLoading(true);
        api.get('/recordings/list')
            .then((r) => { setIntercom(r.data.intercom || []); setPbx(r.data.pbx || []); })
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const del = async (rec) => {
        if (!confirm('Smazat nahrávku?')) return;
        try {
            await api.delete(`/recordings/${rec.id}`);
            toast.success('Smazáno');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Chyba');
        }
    };

    const fmtDur = (s) => {
        const n = parseInt(s || 0, 10);
        const m = Math.floor(n / 60).toString().padStart(2, '0');
        const sc = (n % 60).toString().padStart(2, '0');
        return `${m}:${sc}`;
    };
    const fmtSize = (n) => {
        const b = parseInt(n || 0, 10);
        if (b > 1048576) return `${(b/1048576).toFixed(1)} MB`;
        if (b > 1024) return `${(b/1024).toFixed(0)} kB`;
        return `${b} B`;
    };

    const list = tab === 'intercom' ? intercom : pbx;

    return (
        <div data-testid="recordings-page">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                    <Microphone size={28} style={{ color: 'var(--brand)' }} />
                    Nahrávky
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Přehrávání a stahování nahrávek (interní interkom + PBX)
                </p>
            </div>

            <Tabs value={tab} onChange={setTab} items={[
                ['intercom', 'Interkom', intercom.length],
                ['pbx', 'PBX / DIDWW', pbx.length],
            ]} />

            <div className="rounded-xl border overflow-hidden"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <table className="w-full text-sm" data-testid="recordings-table">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider"
                            style={{ color: 'var(--text-disabled)' }}>
                            <th className="px-5 py-3">Datum</th>
                            {tab === 'intercom' ? (
                                <>
                                    <th className="px-5 py-3">Vlastník</th>
                                    <th className="px-5 py-3">Velikost</th>
                                </>
                            ) : (
                                <>
                                    <th className="px-5 py-3">Caller ID</th>
                                    <th className="px-5 py-3">DID</th>
                                </>
                            )}
                            <th className="px-5 py-3">Trvání</th>
                            <th className="px-5 py-3">Akce</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="px-5 py-8 text-center text-sm"
                                    style={{ color: 'var(--text-disabled)' }}>Načítám…</td></tr>
                        ) : list.length === 0 ? (
                            <tr><td colSpan={5} className="px-5 py-8 text-center text-sm"
                                    style={{ color: 'var(--text-disabled)' }}>
                                {tab === 'intercom'
                                    ? 'Žádné interkom nahrávky. Hovory přes /intercom se nahrávají automaticky.'
                                    : 'Žádné PBX nahrávky.'}
                            </td></tr>
                        ) : list.map((c) => (
                            <React.Fragment key={c.id}>
                                <tr className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="px-5 py-3 font-mono text-xs">
                                        {new Date(c.created_at || c.started_at).toLocaleString('cs-CZ')}
                                    </td>
                                    {tab === 'intercom' ? (
                                        <>
                                            <td className="px-5 py-3 text-xs font-mono">{c.owner_email}</td>
                                            <td className="px-5 py-3 font-mono text-xs">{fmtSize(c.size_bytes)}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-5 py-3 font-mono">{c.caller_id || '—'}</td>
                                            <td className="px-5 py-3 font-mono">{c.did_number || '—'}</td>
                                        </>
                                    )}
                                    <td className="px-5 py-3 font-mono">{fmtDur(c.duration_seconds)}</td>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-2">
                                            <button
                                                data-testid={`rec-play-${c.id}`}
                                                onClick={() => setPlaying(playing === c.id ? null : c.id)}
                                                className="px-3 py-1 rounded text-xs flex items-center gap-1
                                                          border hover:bg-[var(--bg-surface-hover)]"
                                                style={{ borderColor: 'var(--border-default)' }}
                                            >
                                                <SpeakerHigh size={14} />
                                                {playing === c.id ? 'Skrýt' : 'Přehrát'}
                                            </button>
                                            {(c.url || c.recording_url) && (
                                                <a
                                                    data-testid={`rec-dl-${c.id}`}
                                                    href={c.url || c.recording_url}
                                                    download
                                                    className="px-3 py-1 rounded text-xs flex items-center gap-1
                                                              border hover:bg-[var(--bg-surface-hover)]"
                                                    style={{ borderColor: 'var(--border-default)' }}
                                                >
                                                    <DownloadSimple size={14} />
                                                </a>
                                            )}
                                            {tab === 'intercom' && (
                                                <button
                                                    data-testid={`rec-del-${c.id}`}
                                                    onClick={() => del(c)}
                                                    className="px-3 py-1 rounded text-xs flex items-center gap-1
                                                              border hover:border-[var(--danger)] hover:text-[var(--danger)]"
                                                    style={{ borderColor: 'var(--border-default)' }}
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {playing === c.id && (c.url || c.recording_url) && (
                                    <tr style={{ background: 'var(--bg-app)' }}>
                                        <td colSpan={5} className="px-5 py-3">
                                            <audio controls
                                                   src={(c.url || c.recording_url).startsWith('/')
                                                       ? process.env.REACT_APP_BACKEND_URL + (c.url || c.recording_url)
                                                       : (c.url || c.recording_url)}
                                                   className="w-full"
                                                   data-testid={`rec-audio-${c.id}`} />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Recordings;
